import { Header, notify } from "@general-backoffice/core";
import type { AppConfig, DynamicFeeBracket, SubscriptionTier, SubscriptionTierInput } from "@investhinks/api-client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap";

import { configEndpoints, subscriptionTiersEndpoints } from "../../../../api";
import TierEditModal from "./TierEditModal";
import TierMigrationModal from "./TierMigrationModal";

// Tier form state type
interface TierFormState {
  name: string;
  description: string;
  monthlyPrice: number | string;
  vpsOverride: number | string;
  isActive: boolean;
  sortOrder: number | string;
  vpsText: string;
  tierText: string;
  profitFeeText: string;
  discountText: string;
  dynamicFee: DynamicFeeBracket[];
  commissionOnlyBilling: boolean;
}

const Settings: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "settings" });
  // Config state - AppConfig includes initialFreeMonths
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [initialFreeMonths, setInitialFreeMonths] = useState<number | string>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  // Subscription tiers state
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [usersWithoutTier, setUsersWithoutTier] = useState<number>(0);
  const [tiersLoading, setTiersLoading] = useState<boolean>(true);
  const [tiersError, setTiersError] = useState<string | null>(null);
  const [tierModalOpen, setTierModalOpen] = useState<boolean>(false);
  const [editingTier, setEditingTier] = useState<SubscriptionTier | null>(null);
  // Default dynamic fee structure
  const DEFAULT_DYNAMIC_FEE: DynamicFeeBracket[] = [
    { below: 5000, fee: 0.25 },
    { below: 10000, fee: 0.2 },
    { below: null, fee: 0.15 },
  ];

  const [tierForm, setTierForm] = useState<TierFormState>({
    name: "",
    description: "",
    monthlyPrice: 0,
    vpsOverride: 0,
    isActive: true,
    sortOrder: 0,
    // Invoice text fields
    vpsText: "",
    tierText: "",
    profitFeeText: "",
    discountText: "",
    // Dynamic fee configuration (required)
    dynamicFee: DEFAULT_DYNAMIC_FEE,
    // Commission-only billing mode
    commissionOnlyBilling: false,
  });
  const [tierSaving, setTierSaving] = useState<boolean>(false);

  // Migration modal state
  const [migrateModalOpen, setMigrateModalOpen] = useState<boolean>(false);

  const loadConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { data } = await configEndpoints.get();
      setConfig(data);
      setInitialFreeMonths(data?.initialFreeMonths || 0);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t("billingConfig.loadError");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const loadTiers = useCallback(async () => {
    try {
      setTiersLoading(true);
      setTiersError(null);
      const { data, meta } = await subscriptionTiersEndpoints.getAll();
      setTiers(data);
      setUsersWithoutTier(meta.usersWithoutTier ?? 0);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t("tiers.loadError");
      setTiersError(errorMessage);
    } finally {
      setTiersLoading(false);
    }
  }, [t]);

  // Load config on mount
  useEffect(() => {
    loadConfig();
    loadTiers();
  }, [loadConfig, loadTiers]);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validation
    const months = parseInt(String(initialFreeMonths), 10);
    if (isNaN(months) || months < 0 || months > 12) {
      setError(t("billingConfig.validationFreeMonths"));
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(false);

      await configEndpoints.update({
        initialFreeMonths: months,
      });

      setConfig(config ? { ...config, initialFreeMonths: months } : { initialFreeMonths: months });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t("billingConfig.saveError");
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setInitialFreeMonths(config?.initialFreeMonths || 0);
    setError(null);
    setSuccess(false);
  };

  const hasChanges = config && initialFreeMonths !== config.initialFreeMonths;

  // Tier modal handlers
  const openTierModal = (tier: SubscriptionTier | null = null) => {
    if (tier) {
      setEditingTier(tier);
      setTierForm({
        name: tier.name || "",
        description: tier.description || "",
        monthlyPrice: tier.monthlyPrice || 0,
        vpsOverride: tier.vpsOverride ?? 0,
        isActive: tier.isActive !== false,
        sortOrder: tier.sortOrder || 0,
        vpsText: tier.vpsText || "",
        tierText: tier.tierText || "",
        profitFeeText: tier.profitFeeText || "",
        discountText: tier.discountText || "",
        dynamicFee: tier.dynamicFee || DEFAULT_DYNAMIC_FEE,
        commissionOnlyBilling: tier.commissionOnlyBilling || false,
      });
    } else {
      setEditingTier(null);
      setTierForm({
        name: "",
        description: "",
        monthlyPrice: 0,
        vpsOverride: 0,
        isActive: true,
        sortOrder: tiers.length,
        vpsText: "",
        tierText: "",
        profitFeeText: "",
        discountText: "",
        dynamicFee: DEFAULT_DYNAMIC_FEE,
        commissionOnlyBilling: false,
      });
    }
    setTierModalOpen(true);
  };

  const closeTierModal = () => {
    setTierModalOpen(false);
    setEditingTier(null);
    setTierForm({
      name: "",
      description: "",
      monthlyPrice: 0,
      vpsOverride: 0,
      isActive: true,
      sortOrder: 0,
      vpsText: "",
      tierText: "",
      profitFeeText: "",
      discountText: "",
      dynamicFee: DEFAULT_DYNAMIC_FEE,
      commissionOnlyBilling: false,
    });
  };

  const handleTierFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setTierForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const handleTierSave = async () => {
    // Validation
    if (!tierForm.name.trim()) {
      setTiersError(t("tierModal.nameRequired"));
      return;
    }

    const monthlyPrice = parseFloat(String(tierForm.monthlyPrice));
    if (isNaN(monthlyPrice) || monthlyPrice < 0) {
      setTiersError(t("tierModal.monthlyPriceError"));
      return;
    }

    const vpsOverride = parseFloat(String(tierForm.vpsOverride));
    if (tierForm.vpsOverride === "" || isNaN(vpsOverride) || vpsOverride < 0) {
      setTiersError(t("tierModal.vpsOverrideError"));
      return;
    }

    try {
      setTierSaving(true);
      setTiersError(null);

      // Generate code (slug) from name
      const generateCode = (name: string): string => {
        return name
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
      };

      const tierData: SubscriptionTierInput & { code?: string } = {
        name: tierForm.name.trim(),
        description: tierForm.description.trim(),
        monthlyPrice,
        vpsOverride,
        isActive: tierForm.isActive,
        sortOrder: parseInt(String(tierForm.sortOrder), 10) || 0,
        // Invoice texts (empty string = undefined/use default)
        vpsText: tierForm.vpsText.trim() || undefined,
        tierText: tierForm.tierText.trim() || undefined,
        profitFeeText: tierForm.profitFeeText.trim() || undefined,
        discountText: tierForm.discountText.trim() || undefined,
        // Dynamic fee configuration
        dynamicFee: tierForm.dynamicFee,
        // Commission-only billing mode
        commissionOnlyBilling: tierForm.commissionOnlyBilling,
      };

      // Add code only when creating (not updating)
      if (!editingTier) {
        tierData.code = generateCode(tierForm.name);
      }

      if (editingTier) {
        await subscriptionTiersEndpoints.update(editingTier.id, tierData);
        notify.success(t("tiers.tierUpdated", { name: tierData.name }));
      } else {
        await subscriptionTiersEndpoints.create(tierData);
        notify.success(t("tiers.tierCreated", { name: tierData.name }));
      }

      await loadTiers();
      closeTierModal();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t("tiers.saveError");
      setTiersError(errorMessage);
    } finally {
      setTierSaving(false);
    }
  };

  const handleTierDelete = async (tier: SubscriptionTier) => {
    // Prevent deleting default tier
    if (tier.isDefault) {
      setTiersError(t("tiers.cannotDeleteDefault"));
      return;
    }

    if (!window.confirm(t("tiers.deleteConfirm", { name: tier.name }))) {
      return;
    }

    try {
      setTiersError(null);
      await subscriptionTiersEndpoints.remove(tier.id);
      await loadTiers();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t("tiers.deleteError");
      setTiersError(errorMessage);
    }
  };

  // Migration modal handlers
  const toggleMigrateModal = () => setMigrateModalOpen(!migrateModalOpen);

  const handleMigrationComplete = (count: number) => {
    loadTiers();
    notify.success(t("tiers.migrateSuccess", { count }));
  };

  const handleSetDefault = async (tier: SubscriptionTier) => {
    try {
      setTiersError(null);
      await subscriptionTiersEndpoints.setDefault(tier.id);
      await loadTiers();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t("tiers.setDefaultError");
      setTiersError(errorMessage);
    }
  };

  // Memoize sorted tiers to avoid sorting on every render
  const sortedTiers = useMemo(() => [...tiers].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)), [tiers]);

  return (
    <React.Fragment>
      <Header.BreadCrumb color="secondary" isDark={false}>
        <Col xs={12}>
          <h2 className="text-white mb-0">{t("title")}</h2>
        </Col>
      </Header.BreadCrumb>

      <Container className="mt--6" fluid>
        {/* Configuration Section */}
        <Row>
          <Col xl={8}>
            <Card>
              <CardHeader>
                <h3 className="mb-0">{t("billingConfig.title")}</h3>
              </CardHeader>
              <CardBody>
                {error && (
                  <Alert color="danger" className="mb-4">
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert color="success" className="mb-4">
                    {t("billingConfig.saveSuccess")}
                  </Alert>
                )}

                {isLoading ? (
                  <div className="text-center py-4">
                    <i className="fas fa-spinner fa-spin fa-2x"></i>
                    <p className="mt-2">{t("billingConfig.loading")}</p>
                  </div>
                ) : (
                  <Form onSubmit={handleSave}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="initialFreeMonths" className="form-control-label">
                            {t("billingConfig.initialFreeMonths")}
                          </Label>
                          <Input
                            type="number"
                            id="initialFreeMonths"
                            name="initialFreeMonths"
                            min="0"
                            max="12"
                            value={initialFreeMonths}
                            onChange={(e) => setInitialFreeMonths(e.target.value)}
                            disabled={isSaving}
                            placeholder="0-12"
                          />
                          <small className="form-text text-muted">{t("billingConfig.initialFreeMonthsHelp")}</small>
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div>
                        <Button color="primary" type="submit" disabled={isSaving || !hasChanges}>
                          {isSaving ? (
                            <>
                              <i className="fas fa-spinner fa-spin mr-2"></i>
                              {t("billingConfig.saving")}
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save mr-2"></i>
                              {t("billingConfig.saveChanges")}
                            </>
                          )}
                        </Button>

                        {hasChanges && (
                          <Button
                            color="secondary"
                            type="button"
                            onClick={handleReset}
                            disabled={isSaving}
                            className="ml-2"
                          >
                            {t("billingConfig.reset")}
                          </Button>
                        )}
                      </div>

                      {config && (
                        <small className="text-muted">
                          {t("billingConfig.current")}:{" "}
                          <strong>
                            {config.initialFreeMonths} {t("billingConfig.months")}
                          </strong>
                        </small>
                      )}
                    </div>
                  </Form>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col xl={4}>
            <Card>
              <CardHeader>
                <h3 className="mb-0">{t("info.title")}</h3>
              </CardHeader>
              <CardBody>
                <div className="mb-3">
                  <i className="fas fa-info-circle text-info mr-2"></i>
                  <strong>{t("info.freeMonths")}</strong>
                </div>
                <ul className="text-sm mb-0">
                  <li dangerouslySetInnerHTML={{ __html: t("info.freeMonthsInfo1") }} />
                  <li>{t("info.freeMonthsInfo2")}</li>
                  <li>{t("info.freeMonthsInfo3")}</li>
                </ul>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Subscription Tiers Section */}
        <Row className="mt-4">
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">{t("tiers.title")}</h3>
                <div>
                  <Button color="secondary" size="sm" onClick={toggleMigrateModal} className="mr-2">
                    <i className="fas fa-exchange-alt mr-2"></i>
                    {t("tiers.migrateUsers")}
                  </Button>
                  <Button color="primary" size="sm" onClick={() => openTierModal()}>
                    <i className="fas fa-plus mr-2"></i>
                    {t("tiers.addTier")}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {tiersError && (
                  <Alert color="danger" className="mb-4">
                    {tiersError}
                    <button type="button" className="close" onClick={() => setTiersError(null)}>
                      <span>&times;</span>
                    </button>
                  </Alert>
                )}

                {tiersLoading ? (
                  <div className="text-center py-4">
                    <i className="fas fa-spinner fa-spin fa-2x"></i>
                    <p className="mt-2">{t("tiers.loading")}</p>
                  </div>
                ) : tiers.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-layer-group fa-3x mb-3"></i>
                    <p>{t("tiers.noTiers")}</p>
                    <Button color="primary" size="sm" onClick={() => openTierModal()}>
                      {t("tiers.createFirst")}
                    </Button>
                  </div>
                ) : (
                  <Table responsive hover>
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: "40px" }}>#</th>
                        <th>{t("tiers.columns.name")}</th>
                        <th>{t("tiers.columns.monthlyPrice")}</th>
                        <th>{t("tiers.columns.vpsOverride")}</th>
                        <th>{t("tiers.columns.users")}</th>
                        <th>{t("tiers.columns.status")}</th>
                        <th className="text-right">{t("tiers.columns.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Row for users without tier */}
                      {usersWithoutTier > 0 && (
                        <tr className="table-warning">
                          <td>-</td>
                          <td>
                            <em className="text-muted">{t("tiers.noTierLabel")}</em>
                          </td>
                          <td>-</td>
                          <td>-</td>
                          <td>
                            <Badge color="warning">{usersWithoutTier}</Badge>
                          </td>
                          <td>-</td>
                          <td>-</td>
                        </tr>
                      )}
                      {sortedTiers.map((tier) => (
                        <tr key={tier.id}>
                          <td>{tier.sortOrder || 0}</td>
                          <td>
                            <strong>{tier.name}</strong>
                            {tier.description && <small className="d-block text-muted">{tier.description}</small>}
                          </td>
                          <td>{tier.monthlyPrice} €</td>
                          <td>
                            {tier.vpsOverride !== null && tier.vpsOverride !== undefined
                              ? `${tier.vpsOverride} €`
                              : "-"}
                          </td>
                          <td>
                            <Badge color={(tier.userCount ?? 0) > 0 ? "info" : "secondary"}>
                              {tier.userCount ?? 0}
                            </Badge>
                          </td>
                          <td>
                            {tier.isDefault ? (
                              <Badge color="warning">
                                <i className="fas fa-star mr-1"></i>
                                {tier.isActive ? t("tiers.active") : t("tiers.inactive")}
                              </Badge>
                            ) : (
                              <Badge color={tier.isActive ? "success" : "secondary"}>
                                {tier.isActive ? t("tiers.active") : t("tiers.inactive")}
                              </Badge>
                            )}
                          </td>
                          <td className="text-right">
                            <Button color="link" size="sm" onClick={() => openTierModal(tier)}>
                              <i className="fas fa-edit"></i>
                            </Button>
                            {tier.isDefault ? (
                              <>
                                <Button
                                  color="link"
                                  size="sm"
                                  className="text-muted"
                                  id={`delete-tooltip-${tier.id}`}
                                  style={{ cursor: "not-allowed", opacity: 0.5 }}
                                  disabled
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                                <UncontrolledTooltip target={`delete-tooltip-${tier.id}`}>
                                  {t("tiers.cannotDeleteDefault")}
                                </UncontrolledTooltip>
                              </>
                            ) : (
                              <Button
                                color="link"
                                size="sm"
                                className="text-danger"
                                onClick={() => handleTierDelete(tier)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}

                <div className="mt-3 text-muted small">
                  <i className="fas fa-info-circle mr-1"></i>
                  {t("tiers.helpText")}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Tier Edit Modal */}
      <TierEditModal
        isOpen={tierModalOpen}
        toggle={closeTierModal}
        editingTier={editingTier}
        tierForm={tierForm}
        onFormChange={handleTierFormChange}
        onSave={handleTierSave}
        saving={tierSaving}
        onSetDefault={handleSetDefault}
      />

      {/* Migration Modal */}
      <TierMigrationModal
        isOpen={migrateModalOpen}
        toggle={toggleMigrateModal}
        tiers={tiers}
        usersWithoutTier={usersWithoutTier}
        onMigrationComplete={handleMigrationComplete}
      />
    </React.Fragment>
  );
};

export default Settings;
